import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { components } from '@invo.space/shared'

import {
  StockBinLocationFormValues,
  StockBinLocationFormSchema,
} from './schema'

interface StockBinLocationFormProps {
  isOpen: boolean
  onClose: () => void
  stockItem?: StockItem
  onSuccess?: () => void
}

export const StockBinLocationForm = ({
  isOpen,
  onClose,
  stockItem,
  onSuccess,
}: StockBinLocationFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'warehouse.stockItems',
  })

  const methods = useForm<StockBinLocationFormValues>({
    resolver: zodResolver(StockBinLocationFormSchema),
    defaultValues: {
      binLocation: '',
    },
  })

  useEffect(() => {
    if (stockItem) {
      methods.reset({
        binLocation: stockItem.binLocation ?? '',
      })
    }
  }, [stockItem, methods, isOpen])

  const onValid = async (values: StockBinLocationFormValues) => {
    if (!stockItem?.id) {
      return
    }

    try {
      await API.patch<components['schemas']['StockItem']>(
        `/api/v2/stock-items/${stockItem.id}`,
        {
          binLocation: values.binLocation || null,
        },
      )

      toast.success(t('binLocation.success'))
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      console.error('Bin location update error:', error)
      toast.error(t('binLocation.error'), {
        description: extractErrorMessage(error) || t('error.description'),
      })
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
    }
  }

  const label =
    stockItem?.skuCode && stockItem?.warehouseName
      ? `${stockItem.skuName || stockItem.skuCode}, ${stockItem.warehouseName}`
      : stockItem?.skuCode || stockItem?.warehouseName || stockItem?.id

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('binLocation.title')}</DialogTitle>
          {label && <DialogDescription>{label}</DialogDescription>}
        </DialogHeader>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onValid)}
            className="w-full space-y-6"
          >
            <FormField
              control={methods.control}
              name="binLocation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.binLocation.label')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('form.binLocation.placeholder')}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('form.cancel')}
              </Button>
              <Button type="submit">
                {t('form.save')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
